// Roofs, pavements, lawns, water and the park pond.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { M, P, canvasTex } from '../lib/factory.js';
import { grassTex, gravelTex, panot, rippleTex } from '../textures/ground.js';
import { rustTex } from './walls.js';

// flat-roof terrace in Catalan "rajola" tiles: 8x8 terracotta tiles, each its own fired tone, pale mortar joints,
// worn lighter patches and a little dark lichen
const roofTex = canvasTex(256, 256, (c) => {
  c.fillStyle = '#d8c7b0';
  c.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 8; i++)
    for (let j = 0; j < 8; j++) {
      const v = rr(-14, 14),
        r = 186 + v + rr(-6, 6),
        gr = 128 + v * 0.8,
        b = 96 + v * 0.6;
      c.fillStyle = `rgb(${r},${gr},${b})`;
      c.fillRect(i * 32 + 1.5, j * 32 + 1.5, 29, 29);
      c.fillStyle = 'rgba(255,230,200,.18)';
      c.fillRect(i * 32 + 1.5, j * 32 + 1.5, 29, 2.5);
      c.fillStyle = 'rgba(80,40,20,.15)';
      c.fillRect(i * 32 + 1.5, j * 32 + 28, 29, 2.5);
      for (let k = 0; k < 10; k++) {
        c.fillStyle = rnd() < 0.5 ? 'rgba(90,50,30,.22)' : 'rgba(255,225,190,.25)';
        c.fillRect(i * 32 + 2 + rnd() * 27, j * 32 + 2 + rnd() * 27, 1.2, 1.2);
      }
    }
  for (let k = 0; k < 8; k++) {
    const x = rnd() * 256,
      y = rnd() * 256,
      rad = rr(20, 50),
      g = c.createRadialGradient(x, y, 0, x, y, rad);
    g.addColorStop(0, 'rgba(240,215,185,.22)');
    g.addColorStop(1, 'rgba(240,215,185,0)');
    c.fillStyle = g;
    c.fillRect(x - rad, y - rad, 2 * rad, 2 * rad);
  }
  for (let k = 0; k < 14; k++) {
    c.fillStyle = 'rgba(70,75,50,.22)';
    c.beginPath();
    c.arc(rnd() * 256, rnd() * 256, rr(1.5, 4), 0, PI * 2);
    c.fill();
  }
});
roofTex.repeat.set(1 / 2.6, 1 / 2.6);
export const corniceMat = M(0xf3ebdd, { flatShading: false }),
  roofMat = M(0xffffff, { map: roofTex, flatShading: false }),
  terraMat = M(0xbf7a55),
  shopMat = M(0xffffff, { map: rustTex, flatShading: false }),
  curbMat = M(0xbdb6aa);
export const walkMat = [M(0xffffff, { map: panot, flatShading: false }), curbMat],
  pavingMat = [M(0xfff8ec, { map: panot, flatShading: false }), curbMat];
export const grassMat = M(0xffffff, { map: grassTex, flatShading: false }),
  lawnSide = M(0x6a8f45),
  gravelMat = M(0xffffff, { map: gravelTex, flatShading: false });
export const waterMat = P(0x3d8fb5, {
  roughness: 0.14,
  metalness: 0.2,
  normalMap: rippleTex,
  normalScale: new THREE.Vector2(0.18, 0.18),
  envMapIntensity: 1.1,
});
// the park pond, painted: deep teal in the middle brightening to a sandy-green rim, a reflected sky band,
// soft ripple rings and the odd glint. Its ShapeGeometry UVs are raw units, so the map is fitted to the 17.4 x 11.4 ellipse.
const pondTex = canvasTex(256, 192, (c) => {
  const g = c.createRadialGradient(128, 96, 10, 128, 96, 128);
  g.addColorStop(0, '#1f6f8f');
  g.addColorStop(0.55, '#2f8ea3');
  g.addColorStop(0.85, '#5fb3a8');
  g.addColorStop(1, '#9cc99a');
  c.save();
  c.scale(1, 192 / 256);
  c.fillStyle = g;
  c.fillRect(0, 0, 256, 256);
  c.restore();
  const sky = c.createLinearGradient(0, 30, 0, 90);
  sky.addColorStop(0, 'rgba(220,240,255,0)');
  sky.addColorStop(0.5, 'rgba(220,240,255,.22)');
  sky.addColorStop(1, 'rgba(220,240,255,0)');
  c.fillStyle = sky;
  c.fillRect(0, 30, 256, 60);
  c.strokeStyle = 'rgba(235,250,255,.35)';
  c.lineWidth = 1.4;
  for (let k = 0; k < 9; k++) {
    const x = rr(50, 206),
      y = rr(40, 152),
      r = rr(6, 18);
    for (const f of [1, 1.6]) {
      c.beginPath();
      c.ellipse(x, y, r * f, r * f * 0.55, 0, rr(0, 1), PI * rr(1.2, 1.9));
      c.stroke();
    }
  }
  c.fillStyle = 'rgba(255,255,255,.7)';
  for (let k = 0; k < 14; k++) c.fillRect(rr(40, 216), rr(30, 160), rr(4, 9), 1.2);
});
pondTex.repeat.set(1 / 17.4, 1 / 11.4);
pondTex.offset.set(0.5, 0.5);
pondTex.wrapS = pondTex.wrapT = THREE.ClampToEdgeWrapping;
export const pondMat = P(0xffffff, {
  map: pondTex,
  roughness: 0.3,
  metalness: 0,
  normalMap: rippleTex,
  normalScale: new THREE.Vector2(0.08, 0.08),
  envMapIntensity: 0.6,
});
export const lawnMat = grassMat,
  stoneSmooth = M(0xe8e0d0, { flatShading: false, roughness: 0.7 });
export const stained = [],
  gloriesMats = [];
